import * as CookieConsent from 'vanilla-cookieconsent';

const CATEGORIES = ['essential', 'functional', 'analytics', 'marketing'];

// angular-translate's registerAvailableLanguageKeys mapping (see learn-i18n-l10n.js).
// We must resolve language the same way it does, but pre-bootstrap (no $translate yet).
const AVAILABLE = [
  'en', 'ar', 'zh_CN', 'zh_TW', 'fr', 'fr_CA', 'de', 'hi_IN', 'id', 'it',
  'ja', 'ko', 'pl', 'pt_BR', 'ru', 'es_LA', 'es_ES', 'es_419', 'sv_SE', 'vi', 'th',
];

function resolveLanguage() {
  const nav = (navigator.language || 'en').replaceAll('-', '_');
  if (AVAILABLE.includes(nav)) return nav;
  const base = nav.split('_')[0];
  const hit = AVAILABLE.find((l) => l === base || l.split('_')[0] === base);
  return hit || 'en';
}

async function loadConsentStrings(lang) {
  // Same files angular-translate loads (./translations/<lang>.json). Fall back to en.
  for (const candidate of [lang, 'en']) {
    try {
      const res = await fetch(`./translations/${candidate}.json`);
      if (res.ok) {
        const all = await res.json();
        return { lang: candidate, t: (k) => all[k] ?? k };
      }
    } catch (e) { /* try next */ }
  }
  return { lang: 'en', t: (k) => k };
}

function buildTranslation(t) {
  return {
    consentModal: {
      title: t('consent.banner.title'),
      description: t('consent.banner.description'),
      acceptAllBtn: t('consent.banner.acceptAll'),
      acceptNecessaryBtn: t('consent.banner.rejectAll'),
      showPreferencesBtn: t('consent.banner.managePreferences'),
    },
    preferencesModal: {
      title: t('consent.modal.title'),
      acceptAllBtn: t('consent.modal.acceptAll'),
      acceptNecessaryBtn: t('consent.modal.rejectAll'),
      savePreferencesBtn: t('consent.modal.save'),
      closeIconLabel: t('consent.modal.close'),
      sections: CATEGORIES.map((cat) => ({
        title: t(`consent.categories.${cat}.title`),
        description: t(`consent.categories.${cat}.description`),
        linkedCategory: cat,
      })),
    },
  };
}

// Flag off: run headless and accept all so deferred text/plain data-category
// scripts in index.html still execute (parity with React's activateAll).
async function activateAll() {
  await CookieConsent.run({
    cookie: { name: 'cc_cookie', useLocalStorage: true },
    autoShow: false,
    categories: {
      essential: { enabled: true, readOnly: true },
      functional: {},
      analytics: {},
      marketing: {},
    },
    // v3 reads language.translations unconditionally inside run(); without
    // this it throws and the accept-all below never executes.
    language: {
      default: 'en',
      translations: { en: { consentModal: {}, preferencesModal: {} } },
    },
  });
  CookieConsent.acceptCategory('all');
}

async function runConsent() {
  const lang = resolveLanguage();
  const { lang: resolved, t } = await loadConsentStrings(lang);
  await CookieConsent.run({
    cookie: { name: 'cc_cookie', useLocalStorage: true },
    categories: {
      essential: { enabled: true, readOnly: true },
      functional: {
        autoClear: { cookies: [{ name: /^intercom-/ }, { name: /^__zlcmid/ }, { name: /^_zendesk/ }] },
      },
      analytics: {
        autoClear: { cookies: [{ name: /^_ga/ }, { name: /^_gid/ }] },
      },
      marketing: {},
    },
    language: {
      default: resolved,
      translations: { [resolved]: buildTranslation(t) },
    },
    onChange: () => {
      // autoClear only wipes cookies; tear down live functional SDKs on revocation so
      // an already-booted Intercom/Zendesk stops sending data mid-session.
      if (!CookieConsent.acceptedCategory('functional')) {
        if (window.Intercom) {
          window.Intercom('shutdown');
        }
        try {
          if (window.zE) {
            window.zE('webWidget', 'logout');
            window.zE('webWidget', 'hide');
          }
        } catch (e) {
          /* zE not fully initialized — nothing to tear down */
        }
      }
    },
  });
}

let _enabled = false;

// Resolve the banner's primary/brand color the same way the React app derives
// Chakra brand.button (App.tsx): partner override first, then overrides.buttons,
// then brand.primary. Partner branding isn't available pre-bootstrap here, so we
// cover the non-partner case (matches React for non-white-labeled tenants).
function applyBrandColor(config) {
  const colors = (config && config.theme && config.theme.colors) || {};
  const buttons = colors.overrides && colors.overrides.buttons;
  const brand = (buttons && buttons !== '') ? buttons : (colors.brand && colors.brand.primary);
  if (brand) {
    // Inherited custom property; consent-theme.css reads it via var() on #cc-main.
    document.documentElement.style.setProperty('--continu-consent-brand', brand);
  }
}

export function initConsent(config) {
  applyBrandColor(config);
  // Flag lives in the pre-auth /v3/config response under features (not feature_flags).
  // React reads the same path: configStore.config.features.consent_management_enabled.
  const enabled = !!(config && config.features && config.features.consent_management_enabled);
  _enabled = enabled;
  // Fire and forget; both paths must run before the static scripts would have.
  return enabled ? runConsent() : activateAll();
}

// Shared helpers for runtime gating (Task 6) and the preferences opener (Task 7).
// CookieConsent is a singleton across the bundle, so importing it elsewhere hits the
// same instance — but exposing these keeps call sites terse and intent-clear.
export const consent = {
  accepted: (cat) => CookieConsent.acceptedCategory(cat),
  showPreferences: () => CookieConsent.showPreferences(),
  setLanguage: (lang) => CookieConsent.setLanguage(lang, true),
  isEnabled: () => _enabled,
};
